<script>
import Layout from '@layouts/loggedin.vue'
import axios from 'axios'
import store from '@state/store'
import mapKeys from 'lodash/mapKeys'
import camelCase from 'lodash/camelCase'
import snakeCase from 'lodash/snakeCase'
import { mapState } from 'vuex'
import { format } from 'date-fns/fp'
import { parseISO } from 'date-fns/esm/fp'

export default {
  components: { Layout },
  props: {
    companyId: {
      type: Number,
      required: true,
    },
    eventId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    isLoading: false,
    isLoadingExit: false,
    isCopying: false,
  }),
  computed: {
    ...mapState({
      event({ events: { events, eventDetails } }) {
        if (!events) return

        const formatPayload = (payload) =>
          this.formatPayload(camelCase, payload)

        return {
          ...formatPayload(eventDetails[this.eventId]),
          otherDetails: {
            ...formatPayload(eventDetails[this.eventId].other_details),
            ...formatPayload(events.find(({ id }) => id === this.eventId)),
          },
        }
      },
    }),
  },
  methods: {
    formatPayload(formater, payload) {
      return mapKeys(payload, (value, key) => formater(key))
    },
    onSaveClick(payload) {
      this.isLoading = true
      const eventForm = this.formatPayload(snakeCase, payload)
      eventForm.start = format(
        'yyyy-MM-dd kk:mm:ss',
        eventForm.start instanceof Date
          ? eventForm.start
          : parseISO(eventForm.start)
      )
      eventForm.end = format(
        'yyyy-MM-dd kk:mm:ss',
        eventForm.end instanceof Date ? eventForm.end : parseISO(eventForm.end)
      )

      return axios
        .post(`/api/v1/opportunity/${eventForm.id}`, eventForm)
        .then(({ data }) => {
          store
            .dispatch('events/setEventDetails', {
              id: data.id,
              eventDetails: data,
            })
            .then(() => alert('Event have been successfully updated!'))
            .catch(() => alert('Something went wrong.'))
        })
        .catch(() => alert('Something went wrong.'))
        .finally(() => (this.isLoading = false))
    },
    onSaveExitClick(payload) {
      this.isLoadingExit = true
      const eventForm = this.formatPayload(snakeCase, payload)
      eventForm.start = format(
        'yyyy-MM-dd kk:mm:ss',
        eventForm.start instanceof Date
          ? eventForm.start
          : parseISO(eventForm.start)
      )
      eventForm.end = format(
        'yyyy-MM-dd kk:mm:ss',
        eventForm.end instanceof Date ? eventForm.end : parseISO(eventForm.end)
      )

      return axios
        .post(`/api/v1/opportunity/${eventForm.id}`, eventForm)
        .then(({ data }) => {
          store
            .dispatch('events/setEventDetails', {
              id: data.id,
              eventDetails: data,
            })
            .then(() => alert('Event have been successfully updated!'))
            .catch(() => alert('Something went wrong.'))
        })
        .catch(() => alert('Something went wrong.'))
        .finally(() => (this.isLoadingExit = false))
    },
    onCancelClick() {
      this.$router.push({
        name: 'eventDetails',
        params: { companyId: this.companyId, eventId: this.eventId },
      })
    },
    onCancelEventClick() {
      if (
        !window.confirm(
          'Are you sure you want to cancel and delete this event?'
        )
      ) {
        return
      }
      this.isLoading = true
      store
        .dispatch('events/cancelEvent', {
          eventId: this.eventId,
        })
        .then(() => {
          this.$router.push({
            name: 'company',
            params: { companyId: this.companyId },
          })
        })
        .catch(() => alert('Something went wrong.'))
        .finally(() => (this.isLoading = false))
    },
    onDetachEventClick() {
      if (!window.confirm('Are you sure you want to detach this event?')) {
        return
      }
      this.isLoading = true
      store
        .dispatch('events/detachEvent', {
          eventId: this.eventId,
        })
        .then((response) => {
          if (response) {
            this.onCancelClick()
          }
        })
        .catch(() => alert('Something went wrong.'))
        .finally(() => (this.isLoading = false))
    },
    onCopyEventClick() {
      this.isCopying = true
      store
        .dispatch('events/copyEvent', {
          eventId: this.eventId,
        })
        .then((response) => {
          if (!response) return
          this.$router.push({
            name: 'editEventDetails',
            params: {
              companyId: this.companyId,
              eventId: response.id,
            },
          })
        })
        .catch(() => alert('Something went wrong.'))
        .finally(() => (this.isCopying = false))
    },
  },
}
</script>

<template>
  <Layout>
    <div class="text-xl px-7.5 py-3.5 bg-white">
      <span class="">Volunteer Opportunities</span>
      <span class="breadcrumb-separator">></span>
      <span class="text-blue">Edit Event</span>
    </div>

    <div
      class="flex items-center justify-between pl-10 pr-12 bg-white h-14 leading-14"
    >
      <div class="flex">
        <BaseLink
          name="editEventDetails"
          class="events-tab"
          active-class="active"
          append
          exact
        >
          Details
        </BaseLink>
        <BaseLink
          name="editEventSettings"
          class="events-tab"
          active-class="active"
          append
        >
          Settings
        </BaseLink>
        <BaseLink
          name="editEventCheckin"
          class="events-tab"
          active-class="active"
          append
        >
          Check In
        </BaseLink>
      </div>

      <button
        class="button secondary-button h-7.5 leading-relax select-none text-sml rounded-small-button px-3"
        :class="{ 'text-gray-light pointer-events-none': isCopying }"
        @click="onCopyEventClick"
      >
        {{ !isCopying ? 'Copy event' : 'Copying...' }}
      </button>
    </div>

    <div v-if="!event" class="m-4.5">Loading...</div>
    <div
      v-else
      class="bg-white rounded-xl shadow-box overflow-hidden m-4.5 py-9 px-29 relative"
    >
      <button
        class="text-blue absolute top-0 right-0 text-3xl mr-4 mt-2 cursor-pointer"
        @click="onCancelClick"
        >X</button
      >
      <RouterView
        :key="$route.fullPath"
        :is-loading="isLoading"
        :is-loading-exit="isLoadingExit"
        :company-id="companyId"
        :event="event"
        @loading="(value) => (isLoading = value)"
        @loadingExit="(value) => (isLoadingExit = value)"
        @save="onSaveClick"
        @saveExit="onSaveExitClick"
        @cancel="onCancelClick"
        @cancelEvent="onCancelEventClick"
        @detachEvent="onDetachEventClick"
      />
    </div>
  </Layout>
</template>
